import type { AdjustTrackEventPayload } from "./types";

/**
 * 只在下方 COMMON / OVERRIDES 配置 eventKey -> eventToken（`as const`），
 * `AdjustEventKey` 由这两处自动推断，无需单独维护 event name 列表。
 *
 * - COMMON：各环境默认 token
 * - OVERRIDES：按环境覆盖；某 key 可只出现在某一环境
 */
const ADJUST_EVENT_TOKENS_COMMON = {
  alphapick_page_view: "aexp5b",
  member_page_view: "4jcfnk",
  page_view: "9v3iy2",
} as const;

const ADJUST_EVENT_TOKENS_OVERRIDES = {
  dev: {},
  beta: {},
  pre: {},
  prod: {},
} as const;

export type AdjustEnv = keyof typeof ADJUST_EVENT_TOKENS_OVERRIDES;

type KeysFromCommon = keyof typeof ADJUST_EVENT_TOKENS_COMMON;
type KeysFromOverrides = {
  [E in keyof typeof ADJUST_EVENT_TOKENS_OVERRIDES]: keyof (typeof ADJUST_EVENT_TOKENS_OVERRIDES)[E];
}[keyof typeof ADJUST_EVENT_TOKENS_OVERRIDES];

type AdjustEventKeyMerged = KeysFromCommon | KeysFromOverrides;

/** 尚未配置任何 key 时退化为 string */
export type AdjustEventKey = [AdjustEventKeyMerged] extends [never]
  ? string
  : AdjustEventKeyMerged;

function getAdjustEnvFromRuntime(): AdjustEnv {
  const raw = String(process.env.NEXT_PUBLIC_ENV || "prod");
  if (raw in ADJUST_EVENT_TOKENS_OVERRIDES) {
    return raw as AdjustEnv;
  }
  return "prod";
}

function resolveAdjustEventToken(
  eventKey: AdjustEventKey | string,
  env: AdjustEnv = getAdjustEnvFromRuntime(),
): string | null {
  const key = eventKey.trim();
  if (!key) {
    return null;
  }
  const envOverrides =
    ADJUST_EVENT_TOKENS_OVERRIDES[
      env as keyof typeof ADJUST_EVENT_TOKENS_OVERRIDES
    ] ?? {};
  const token =
    (envOverrides as Record<string, string | undefined>)[key] ??
    (ADJUST_EVENT_TOKENS_COMMON as Record<string, string | undefined>)[key];
  if (!token) {
    return null;
  }
  const trimmed = token.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function buildAdjustPayloadFromEventKey(
  eventKey: AdjustEventKey | string,
  payload: Omit<AdjustTrackEventPayload, "eventKey" | "eventToken"> = {},
  env: AdjustEnv = getAdjustEnvFromRuntime(),
): AdjustTrackEventPayload | null {
  const eventToken = resolveAdjustEventToken(eventKey, env);
  if (!eventToken) {
    return null;
  }
  return {
    ...payload,
    eventKey,
    eventToken,
  };
}
